import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { PE_IMG, Vine, PEHead, GoldRule, peFade } from "./bits";
import PEMenu from "./PEMenu";
import PEClosing from "./PEClosing";
import BookingFlow from "./BookingFlow";
import Reveal from "../Reveal";
import { scrollToSection } from "../../lib/scroll";

const SIGNATURE_INCLUDES = [
  "Customized hair service",
  "Shampoo",
  "Blow-dry",
  "Conditioning & treatment",
  "Waxing maintenance",
  "Facial or approved beauty treatment",
];

function PENav({ onBook }) {
  return (
    <header data-testid="pe-nav" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-pe-ivory/85 border-b border-pe-gold/25">
      <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-6">
        <a href="#pe-top" data-testid="pe-nav-logo" onClick={scrollToSection("#pe-top")} className="flex items-center gap-3">
          <img src="/images/pe-logo.png" alt="Prismatic Essence monogram" className="h-9 w-auto" />
          <span className="font-pe text-sm tracking-[0.35em] uppercase text-pe-forest hidden sm:block">Prismatic Essence</span>
        </a>
        <nav className="hidden md:flex items-center gap-8" aria-label="Prismatic Essence">
          {[["Experience", "#pe-experience"], ["The Menu", "#pe-menu"], ["Journey", "#pe-journey"], ["About", "#pe-about"]].map(([label, href]) => (
            <a
              key={href}
              href={href}
              data-testid={`pe-nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={scrollToSection(href)}
              className="font-sans text-[10px] tracking-[0.25em] uppercase text-pe-olive hover:text-pe-forest transition-colors"
            >
              {label}
            </a>
          ))}
          <Link to="/prismatic-essence/account" data-testid="pe-nav-account-link" className="font-sans text-[10px] tracking-[0.25em] uppercase text-pe-olive hover:text-pe-forest transition-colors">
            Account
          </Link>
        </nav>
        <button
          data-testid="pe-nav-book-button"
          onClick={() => onBook()}
          className="rounded-full border border-pe-brass/60 text-pe-forest px-5 py-2 font-sans text-[10px] tracking-[0.25em] uppercase hover:bg-pe-forest hover:text-pe-ivory transition-colors"
        >
          Book
        </button>
      </div>
    </header>
  );
}

function PEHero({ onBook }) {
  return (
    <section id="pe-top" data-testid="pe-hero" className="relative min-h-screen bg-pe-ivory flex items-center overflow-hidden">
      <img
        src={PE_IMG.hero}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover object-[50%_20%] opacity-40"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-pe-ivory/85 via-pe-ivory/65 to-pe-ivory" aria-hidden="true" />

      <Vine className="absolute -left-4 top-16 h-[75%] opacity-70 hidden md:block" />
      <Vine className="absolute -right-4 top-24 h-[70%] opacity-70 hidden md:block" flip />

      <div className="relative max-w-3xl mx-auto px-5 text-center py-32">
        <motion.div
          variants={peFade}
          initial="hidden"
          animate="show"
          custom={0}
          className="inline-block mb-10 px-10 md:px-14 py-8 md:py-10 rounded-sm bg-pe-ivory/60 backdrop-blur-[2px] border border-pe-gold/25 shadow-[0_24px_70px_rgba(44,58,40,0.10)]"
        >
          <img
            src="/images/pe-logo.png"
            alt="Prismatic Essence — gold monogram"
            data-testid="pe-logo"
            className="h-36 md:h-48 w-auto mx-auto"
          />
        </motion.div>
        <motion.p variants={peFade} initial="hidden" animate="show" custom={1} className="font-sans text-[11px] tracking-[0.35em] uppercase text-pe-olive mb-6">
          A Luxury Beauty Experience
        </motion.p>
        <motion.h1
          data-testid="pe-hero-title"
          variants={peFade}
          initial="hidden"
          animate="show"
          custom={2}
          className="font-pe font-light text-4xl sm:text-5xl lg:text-7xl tracking-[0.08em] text-pe-forest"
        >
          PRISMATIC ESSENCE
        </motion.h1>
        <motion.div variants={peFade} initial="hidden" animate="show" custom={3}>
          <GoldRule className="w-32 mx-auto my-7" center />
        </motion.div>
        <motion.p variants={peFade} initial="hidden" animate="show" custom={3} className="font-pe italic text-xl md:text-2xl text-pe-brass tracking-[0.15em] mb-8">
          Beauty in Every Dimension.
        </motion.p>
        <motion.p variants={peFade} initial="hidden" animate="show" custom={4} className="font-sans font-light text-base md:text-lg text-pe-body leading-relaxed max-w-xl mx-auto mb-12">
          Hair, skin, beauty, and self-expression come together in one elevated experience.
        </motion.p>
        <motion.div variants={peFade} initial="hidden" animate="show" custom={5} className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#pe-experience"
            data-testid="pe-cta-menu"
            onClick={scrollToSection("#pe-experience")}
            className="rounded-full bg-pe-forest text-pe-ivory px-9 py-3.5 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors"
          >
            Explore the Experience
          </a>
          <button
            data-testid="pe-cta-book"
            onClick={() => onBook()}
            className="rounded-full border border-pe-brass/70 text-pe-forest px-9 py-3.5 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-gold/10 transition-colors"
          >
            Book Your Service
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function PEPhilosophy() {
  return (
    <section id="pe-experience" data-testid="pe-philosophy" className="relative bg-pe-ivory py-24 md:py-32 overflow-hidden">
      <Vine className="absolute -right-6 top-10 h-[80%] opacity-50 hidden lg:block" flip />
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <PEHead center={false} overline="The Prismatic Essence Experience" title={<>The Prismatic <span className="italic">Experience.</span></>} />
          <Reveal>
            <p className="font-sans font-light text-pe-body leading-relaxed text-base md:text-lg mb-6 -mt-6">
              Beauty should feel good from beginning to end. Prismatic Essence brings hair, skin, beauty, maintenance,
              and self-expression together through a personalized experience designed to leave you feeling confident,
              cared for, and completely yourself.
            </p>
            <p className="font-sans font-light text-pe-body leading-relaxed text-base md:text-lg">
              You came here to be cared for. To look good. To feel good. To leave feeling like yourself — elevated.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="relative max-w-md mx-auto">
            <div className="absolute -inset-3 border border-pe-gold/40 rounded-sm translate-x-4 translate-y-4" aria-hidden="true" />
            <img
              src={PE_IMG.experience}
              alt="A guest receiving care at Prismatic Essence"
              data-testid="pe-philosophy-image"
              className="relative w-full aspect-[4/5] object-cover rounded-sm"
              loading="lazy"
            />
            <p className="mt-5 font-pe italic text-lg text-pe-olive text-center">care, in progress</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PESignature({ onBook }) {
  return (
    <section id="pe-signature" data-testid="pe-signature" className="relative bg-pe-cream py-24 md:py-32 overflow-hidden">
      <Vine className="absolute -left-6 top-16 h-[75%] opacity-50 hidden lg:block" />
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Reveal>
          <div data-testid="pe-signature-card" className="relative bg-pe-ivory border border-pe-gold/40 rounded-sm px-7 py-14 md:p-16 text-center shadow-[0_20px_60px_rgba(44,58,40,0.08)]">
            <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-pe-olive mb-6">The Signature Experience</p>
            <h2 className="font-pe font-normal text-3xl sm:text-4xl lg:text-5xl tracking-wide text-pe-forest mb-4">
              The Prismatic Experience
            </h2>
            <p className="font-pe text-2xl md:text-3xl text-pe-brass mb-2">
              $600 <span className="font-sans text-xs tracking-[0.25em] uppercase text-pe-muted align-middle">· up to 3 hours · $25 deposit to book</span>
            </p>
            <GoldRule className="w-24 mx-auto my-8" center />
            <p className="font-sans font-light text-pe-body leading-relaxed max-w-lg mx-auto mb-10">
              One elevated appointment, designed around you. It may include:
            </p>
            <ul className="max-w-md mx-auto space-y-4 mb-12 text-left">
              {SIGNATURE_INCLUDES.map((item) => (
                <li key={item} className="flex items-baseline gap-4">
                  <span className="text-pe-brass text-xs" aria-hidden="true">✦</span>
                  <span className="font-pe text-lg md:text-xl text-pe-forest flex-1">{item}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="pe-signature-book-button"
              onClick={() => onBook("prismatic-experience")}
              className="rounded-full bg-pe-forest text-pe-ivory px-9 py-3.5 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors"
            >
              Book the Experience
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function PrismaticPage() {
  const [bookingFor, setBookingFor] = useState(null); // null = closed, "" = open, otherwise service id
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    const book = params.get("book");
    if (book !== null) {
      setBookingFor(book || "");
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openBook = (serviceId = "") => setBookingFor(serviceId);

  return (
    <div data-testid="pe-page" className="bg-pe-ivory text-pe-forest font-sans antialiased">
      <PENav onBook={openBook} />
      <main id="main-content">
        <PEHero onBook={openBook} />
        <PEPhilosophy />
        <PESignature onBook={openBook} />
        <PEMenu onBook={openBook} />
        <PEClosing onBook={openBook} />
      </main>
      {bookingFor !== null && <BookingFlow initialService={bookingFor || undefined} onClose={() => setBookingFor(null)} />}
    </div>
  );
}
